import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { CreditCard, ShieldAlert, CheckCircle, Loader2, RefreshCw, AlertTriangle, ArrowLeft } from 'lucide-react';

/**
 * Admin-only page to run the Iter 113 Stripe pricing rollout.
 *
 * Three actions, all using `axiosInstance` so the admin's existing session
 * token is sent automatically — no manual token-pasting in DevTools.
 *
 *   • Verify   → GET  /api/admin/stripe/verify-prices
 *   • Dry-Run  → POST /api/admin/stripe/mint-new-prices  body: {}
 *   • Apply    → POST /api/admin/stripe/mint-new-prices  body: { apply: true }
 */
const AdminStripeRollout = () => {
  const navigate = useNavigate();
  const [verifyState, setVerifyState] = useState({ data: null, error: null, loading: true });
  const [rollout, setRollout] = useState({ data: null, error: null, loading: false, kind: '' });
  const [confirmApply, setConfirmApply] = useState(false);

  const refreshVerify = async () => {
    setVerifyState(s => ({ ...s, loading: true, error: null }));
    try {
      const res = await axiosInstance.get('/admin/stripe/verify-prices');
      setVerifyState({ data: res.data, error: null, loading: false });
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message;
      setVerifyState({ data: null, error: code === 401 ? 'Not authenticated as admin. Please log in again.' : msg, loading: false });
    }
  };

  useEffect(() => { refreshVerify(); }, []);

  const runRollout = async (apply) => {
    setRollout({ data: null, error: null, loading: true, kind: apply ? 'apply' : 'dryRun' });
    try {
      const res = await axiosInstance.post('/admin/stripe/mint-new-prices', apply ? { apply: true } : {});
      setRollout({ data: res.data, error: null, loading: false, kind: apply ? 'apply' : 'dryRun' });
      if (apply) await refreshVerify();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setRollout(s => ({ ...s, error: msg, loading: false }));
    }
  };

  const allOk = verifyState.data?.allOk;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-stripe-rollout-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-purple-700 mb-4">
          <ArrowLeft className="h-4 w-4" /> Admin Dashboard
        </button>

        <div className="flex items-start gap-3 mb-6">
          <span className="inline-flex w-12 h-12 rounded-xl bg-purple-600 text-white items-center justify-center shadow-md">
            <CreditCard className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Stripe Pricing Rollout</h1>
            <p className="text-sm text-gray-600">Iteration 113 — Basic €9.99 · 12-Month €29.99 (IVA included)</p>
          </div>
        </div>

        {/* === Section A: current state === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm mb-6">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-gray-900">Current Stripe state (LIVE)</h2>
            <button onClick={refreshVerify} disabled={verifyState.loading}
              className="text-xs text-purple-700 inline-flex items-center gap-1 hover:underline disabled:opacity-50"
              data-testid="stripe-rollout-refresh-btn">
              {verifyState.loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Refresh
            </button>
          </div>

          {verifyState.loading && <div className="text-sm text-gray-500 py-6 text-center">Checking Stripe...</div>}
          {verifyState.error && (
            <div className="rounded-lg p-3 bg-red-50 border border-red-200 text-sm text-red-700" data-testid="stripe-rollout-verify-error">
              <AlertTriangle className="h-4 w-4 inline mr-1" /> {verifyState.error}
            </div>
          )}
          {verifyState.data && (
            <div data-testid="stripe-rollout-verify-result">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium mb-3 ${allOk ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                {allOk ? <CheckCircle className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
                {allOk ? 'All prices match expected values.' : 'Mismatch detected — apply rollout to fix.'}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-gray-500 uppercase">
                  <tr><th className="text-left py-1">Plan</th><th className="text-left py-1">Stripe Price ID</th><th className="text-right py-1">Amount</th><th className="text-right py-1">Expected</th><th className="text-right py-1">OK</th></tr>
                </thead>
                <tbody>
                  {verifyState.data.results.map((r) => (
                    <tr key={r.plan} className="border-t border-gray-100">
                      <td className="py-2 font-medium">{r.plan}</td>
                      <td className="py-2 font-mono text-xs text-gray-600 break-all">{r.price_id || r.error || '—'}</td>
                      <td className="py-2 text-right">{r.amount_eur != null ? `€${r.amount_eur}` : '—'}</td>
                      <td className="py-2 text-right text-gray-500">€{r.expected_amount_eur}</td>
                      <td className="py-2 text-right">{r.ok ? <CheckCircle className="h-4 w-4 text-green-600 inline" /> : <ShieldAlert className="h-4 w-4 text-amber-600 inline" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* === Section B: rollout actions === */}
        <section className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-1">Run rollout</h2>
          <p className="text-xs text-gray-500 mb-4">
            Always click <strong>Dry-Run</strong> first. <strong>Apply</strong> creates real Stripe LIVE prices and persists the new IDs to the database — no restart required.
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => runRollout(false)}
              disabled={rollout.loading}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="stripe-rollout-dryrun-btn"
            >
              {rollout.loading && rollout.kind === 'dryRun' ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Dry-Run (preview)
            </button>
            <button
              onClick={() => setConfirmApply(true)}
              disabled={rollout.loading || allOk}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="stripe-rollout-apply-btn"
              title={allOk ? 'Already aligned — no rollout needed' : 'Mint new prices and persist'}
            >
              Apply rollout
            </button>
          </div>

          {rollout.error && (
            <div className="rounded-lg p-3 bg-red-50 border border-red-200 text-sm text-red-700 mb-3" data-testid="stripe-rollout-action-error">
              <AlertTriangle className="h-4 w-4 inline mr-1" /> {rollout.error}
            </div>
          )}

          {rollout.data && (
            <div className="rounded-lg p-3 bg-gray-50 border border-gray-200 mb-3" data-testid="stripe-rollout-action-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-gray-900">
                  {rollout.kind === 'apply' ? 'Apply complete' : 'Dry-run complete'} · stripeMode = {rollout.data.stripeMode}
                </span>
              </div>
              <ul className="text-xs space-y-1">
                {rollout.data.results.map((r) => (
                  <li key={r.plan} className="flex justify-between gap-2">
                    <span className="font-medium">{r.plan}</span>
                    <span className="text-gray-600 truncate">
                      {r.status} {r.price_id ? `· ${r.price_id}` : ''} {r.amount_eur ? `· €${r.amount_eur}` : ''}
                      {r.error ? ` · ${r.error}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              {rollout.data.note && <p className="text-xs text-gray-500 mt-2">{rollout.data.note}</p>}
            </div>
          )}
        </section>

        {/* Apply confirmation modal */}
        {confirmApply && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-6" data-testid="stripe-rollout-apply-modal">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-amber-600" /> Apply rollout?
              </h3>
              <p className="text-sm text-gray-600 mb-1">This will:</p>
              <ul className="text-sm text-gray-600 list-disc list-inside mb-4 space-y-1">
                <li>Create new LIVE Stripe Prices (€9.99 + €29.99, IVA-inclusive)</li>
                <li>Persist the new IDs in the database</li>
                <li>Take effect immediately (no restart)</li>
              </ul>
              <p className="text-xs text-gray-500 mb-4">Existing subscriptions are not affected.</p>
              <div className="flex gap-2">
                <button onClick={() => setConfirmApply(false)} className="flex-1 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
                <button
                  onClick={() => { setConfirmApply(false); runRollout(true); }}
                  className="flex-1 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
                  data-testid="stripe-rollout-apply-confirm-btn"
                >
                  Yes, apply now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStripeRollout;
